import { GENERATED_FILES_CHAT_DOWNLOAD_LIMIT } from "@frontend-shared/constants";
import { memo, useCallback, useMemo } from "react";
import { useTranslation } from "react-i18next";

import { useGetGeneratedFiles } from "@/api/generated-files";
import { Button } from "@/components/ui/button";
import {
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { Skeleton } from "@/components/ui/skeleton";

import { ChatGeneratedFilesDialogFooter } from "./ChatGeneratedFilesDialogFooter";
import { ChatGeneratedFilesList } from "./ChatGeneratedFilesList";
import type { IChatGeneratedFilesDialogBodyProps } from "./types";
import { useChatGeneratedFilesSelection } from "./use-chat-generated-files-selection";

const SKELETON_ROW_COUNT = 3;

// NOTE: NSAI-5312 — rendered inside DialogContent, which only mounts its
// children while the dialog is open, so the chat's listing is fetched when the
// dialog opens and not once per chat in the sidebar.
const ChatGeneratedFilesDialogBodyComponent = ({
  chatUuid,
  isDownloading,
  progressLabel,
  onDownload,
  onCancel,
}: IChatGeneratedFilesDialogBodyProps): JSX.Element => {
  const { t } = useTranslation("common");
  const { data, isLoading, isError, refetch } = useGetGeneratedFiles({
    chatUuid,
    limit: GENERATED_FILES_CHAT_DOWNLOAD_LIMIT,
  });

  const files = useMemo(() => data?.data ?? [], [data]);
  const { selectedFiles, selection, checkedState, toggleFile, setAllSelected } =
    useChatGeneratedFilesSelection({ files });

  const handleDownload = useCallback((): void => {
    onDownload(selectedFiles);
  }, [onDownload, selectedFiles]);

  const handleRetry = useCallback((): void => {
    void refetch();
  }, [refetch]);

  const renderContent = (): JSX.Element => {
    if (isLoading) {
      return (
        <div className="space-y-2" data-testid="chat-generated-files-loading">
          {Array.from({ length: SKELETON_ROW_COUNT }).map((_unused, index) => (
            <Skeleton
              key={`skeleton-${String(index)}`}
              className="h-10 w-full"
            />
          ))}
        </div>
      );
    }

    // NOTE: A failed background refetch keeps the files already on screen.
    if (isError && data === undefined) {
      return (
        <div
          className="flex flex-col items-center gap-3 py-6 text-center text-sm text-muted-foreground"
          role="alert"
          data-testid="chat-generated-files-error"
        >
          <span>{t("generated_files.load_failed")}</span>
          <Button
            type="button"
            variant="outline"
            size="sm"
            onClick={handleRetry}
            data-testid="chat-generated-files-retry"
          >
            {t("generated_files.retry")}
          </Button>
        </div>
      );
    }

    if (files.length === 0) {
      return (
        <p
          className="py-6 text-center text-sm text-muted-foreground"
          data-testid="chat-generated-files-empty"
        >
          {t("generated_files.download_dialog.empty")}
        </p>
      );
    }

    return (
      <ChatGeneratedFilesList
        files={files}
        totalCount={data?.total ?? files.length}
        selectedFiles={selectedFiles}
        selection={selection}
        checkedState={checkedState}
        onToggleFile={toggleFile}
        onSetAllSelected={setAllSelected}
      />
    );
  };

  return (
    <>
      <DialogHeader>
        <DialogTitle>{t("generated_files.download_dialog.title")}</DialogTitle>
        <DialogDescription>
          {t("generated_files.download_dialog.description")}
        </DialogDescription>
      </DialogHeader>
      {renderContent()}
      <ChatGeneratedFilesDialogFooter
        selectedCount={selectedFiles.length}
        isDownloading={isDownloading}
        progressLabel={progressLabel}
        onDownload={handleDownload}
        onCancel={onCancel}
      />
    </>
  );
};

export const ChatGeneratedFilesDialogBody = memo(
  ChatGeneratedFilesDialogBodyComponent,
);
